import {
  CheckCircleOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  ImportOutlined,
  MailOutlined,
  ReloadOutlined,
  RobotOutlined,
  SaveOutlined,
  PlusOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { Alert, Button, Card, Checkbox, Divider, Input, Radio, Select, Space, Tag, Typography, Upload, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import SendCVModal from '../components/CV/SendCVModal';
import { contactsApi } from '../services/contactsApi';
import './CandidateIntelligenceProfileBuilder.css';

const { Paragraph } = Typography;
const { TextArea } = Input;

const EXAMPLE_PROFILE = {
  profileVersion: '1.0',
  generatedAt: new Date().toISOString(),
  profileStatus: 'draft',
  profileApproved: false,
  profileApprovedAt: null,
  identityMode: 'anonymous',
  candidateId: 'A',
  anonymousLabel: 'Candidate A',
  displayName: 'Alex Morgan',
  targetRelationship: 'profiled_for',
  targetRole: 'Head of Business Development',
  descriptor: 'Physical Precious Metals · Bullion · Refining',
  portraitUrl: '',
  portraitAlt: '',
  portraitConsent: false,
  market: 'Singapore · APAC',
  yearsExperience: 18,
  mobility: 'Open to regional travel',
  availability: 'To be confirmed',
  fitSignal: 'validate',
  assessment:
    'Commercial precious-metals leader with evidence of regional client development across bullion, refining and institutional channels. Career progression shows sustained responsibility for market entry, strategic accounts and cross-border partnerships; direct P&L ownership and near-term availability should be validated in interview.',
  whyRelevant: 'Combines regional precious-metals market knowledge with institutional relationship development and evidence of commercial execution across APAC.',
  mandateFit: [
    'Built institutional relationships across APAC precious-metals markets',
    'Led commercial growth spanning bullion distribution and refining',
    'Developed senior relationships with banks, mints and trading houses',
    'Combined market-entry execution with long-cycle account development'
  ],
  marketFootprint: [
    { label: 'Bullion Trading', icon: 'bullion' },
    { label: 'Toll Refining', icon: 'refining' },
    { label: 'Sovereign Mints', icon: 'institution' },
    { label: 'Institutional Relationships', icon: 'network' }
  ],
  keySkills: ['Bullion Trading', 'Toll Refining', 'Institutional Relationships', 'APAC Market Development'],
  experience: [
    {
      title: 'Regional Commercial Director',
      organisation: 'Confidential precious-metals group',
      anonymousOrganisation: 'Global Precious Metals Group',
      startDate: '2019',
      endDate: 'Present',
      remit: 'Regional growth leadership across institutional bullion and refining relationships.',
      bullets: [
        'Expanded strategic coverage across Singapore, Hong Kong and Southeast Asia.',
        'Secured multi-year relationships with institutional and sovereign counterparties.',
        'Built a qualified commercial pipeline spanning bullion, refining and logistics.'
      ]
    }
  ],
  education: [{ qualification: 'Bachelor of Business', institution: 'University withheld', year: '2006' }],
  credentials: ['Responsible sourcing training'],
  languages: ['English', 'Mandarin'],
  systems: ['CRM platforms', 'Market data terminals'],
  commercialInformation: '',
  commercialApproved: false,
  confidentialityText: 'Confidential candidate submission. For the intended recipient only. ALICE Recruiter (EA Licence No. R1548798)',
  lowInkPrint: false,
  experienceSource: 'fallback',
  experienceApproved: false,
  experienceApprovedAt: null
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const EMPTY_EXPERIENCE = {
  title: '',
  organisation: '',
  anonymousOrganisation: '',
  startDate: '',
  endDate: '',
  remit: '',
  bullets: []
};

const EMPTY_EDUCATION = {
  qualification: '',
  institution: '',
  year: ''
};

const firstArray = (...values) => values.find((value) => Array.isArray(value) && value.length > 0) || [];

const toTextArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (item === null || item === undefined) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          return [
            item.text,
            item.content,
            item.description,
            item.summary,
            item.bullet,
            item.value
          ].filter(Boolean);
        }
        return [String(item)];
      })
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
};

const normalizeExperienceEntry = (exp, index = 0) => {
  const bullets = toTextArray(
    exp?.bullets ||
    exp?.highlights ||
    exp?.achievements ||
    exp?.description ||
    exp?.descriptionText ||
    exp?.description_text ||
    exp?.summary ||
    exp?.details ||
    exp?.content ||
    exp?.text ||
    exp?.bullet_points
  );

  const title = exp?.title || exp?.role || exp?.position || exp?.jobTitle || exp?.job_title || `Role ${index + 1}`;
  const organisation = exp?.company || exp?.organisation || exp?.organization || exp?.employer || exp?.companyName || exp?.employerName || '';
  const anonymousOrganisation = exp?.anonymousOrganisation || exp?.anonymous_organisation || organisation || 'Organisation withheld';

  return {
    title,
    organisation,
    anonymousOrganisation,
    company: organisation,
    startDate: exp?.starts_at || exp?.startDate || exp?.start || exp?.from || exp?.periodStart || exp?.years || '',
    endDate: exp?.ends_at || exp?.endDate || exp?.end || exp?.to || exp?.periodEnd || exp?.years || 'Present',
    remit: exp?.remit || exp?.scope || exp?.overview || exp?.summary || exp?.description_text || exp?.descriptionText || '',
    bullets
  };
};

const extractSkillsFromText = (text) => {
  const candidates = [
    'Trade Finance', 'Investment Banking', 'Financial Modeling', 'Corporate Finance', 'Commercial Banking',
    'Private Equity', 'Asset Management', 'Credit Analysis', 'Compliance', 'Commodity Trading',
    'Precious Metals Trading', 'Gold Trading', 'Hedging', 'Derivatives', 'Risk Management', 'Bloomberg',
    'Microsoft Excel', 'Market Making', 'Client Pricing', 'Liquidity Management', 'Treasury Operations',
    'Structured Products', 'FX', 'FX Advisory', 'Family Office', 'Institutional Sales', 'Business Development'
  ];
  const haystack = String(text || '').toLowerCase();
  return candidates.filter((item) => haystack.includes(item.toLowerCase())).slice(0, 10);
};

const mapCvDataToProfile = (cvData = {}) => {
  const job = cvData?.lastEvaluatedJob || cvData?.contact?.lastEvaluatedJob || {};
  const rawWorkExperience = firstArray(
    cvData?.workExperience,
    cvData?.work_experience,
    cvData?.professionalExperience,
    cvData?.professional_experience,
    cvData?.experience,
    cvData?.workHistory,
    cvData?.work_history,
    cvData?.employmentHistory,
    cvData?.employment_history,
    cvData?.roles,
    cvData?.positions
  );

  const workExperience = rawWorkExperience.map((exp, index) => normalizeExperienceEntry(exp, index));

  const derivedSkills = extractSkillsFromText(cvData?.text || cvData?.fullText || '');
  const sourceSkills = normalizeArray(
    cvData?.skills ||
    cvData?.ai_skills ||
    cvData?.tags?.skills ||
    cvData?.matchingContext?.key_matches
  ).map((skill) => String(skill || '').trim()).filter(Boolean);
  const experienceBullets = workExperience.flatMap((exp) => exp.bullets || []);
  const rawCompensation = cvData?.salary_compensation
    ?? cvData?.salaryCompensation
    ?? cvData?.compensation
    ?? cvData?.salary_expectations
    ?? cvData?.salaryExpectation
    ?? cvData?.expected_salary
    ?? cvData?.expectedSalary
    ?? cvData?.current_salary
    ?? cvData?.currentSalary
    ?? cvData?.salary
    ?? '';
  const compensationInformation = rawCompensation && typeof rawCompensation === 'object'
    ? [
      rawCompensation.currency || rawCompensation.currency_code,
      [
        rawCompensation.min ?? rawCompensation.minimum ?? rawCompensation.from,
        rawCompensation.max ?? rawCompensation.maximum ?? rawCompensation.to
      ].filter((item) => item !== undefined && item !== null && item !== '').join(' - '),
      rawCompensation.period || rawCompensation.frequency
    ].filter(Boolean).join(' ')
    : String(rawCompensation || '');

  return {
    ...EXAMPLE_PROFILE,
    generatedAt: new Date().toISOString(),
    candidateId: cvData?.id || cvData?.contact?.id || 'A',
    anonymousLabel: cvData?.anonymousLabel || cvData?.anonymous_label || 'Candidate A',
    displayName: cvData?.contactName || cvData?.contact?.contactName || 'Candidate',
    targetRole: job?.title || cvData?.title || 'Candidate Profile',
    descriptor: job?.company || cvData?.title || '',
    market: cvData?.location || job?.location || 'Singapore',
    portraitUrl: cvData?.portraitUrl || cvData?.photoUrl || cvData?.photo_url || '',
    portraitAlt: cvData?.portraitAlt || cvData?.contactName || cvData?.contact?.contactName || 'Candidate portrait',
    yearsExperience: cvData?.yearsOfExperience ?? undefined,
    assessment: cvData?.evaluationSummary || EXAMPLE_PROFILE.assessment,
    whyRelevant: cvData?.whyRelevant || cvData?.evaluationSummary || EXAMPLE_PROFILE.whyRelevant,
    mandateFit: (cvData?.evaluationSummary ? [cvData.evaluationSummary] : [...sourceSkills, ...derivedSkills, ...experienceBullets]).filter(Boolean).slice(0, 5),
    keySkills: [...sourceSkills, ...derivedSkills].filter(Boolean).slice(0, 8),
    experience: workExperience.length ? workExperience : EXAMPLE_PROFILE.experience,
    education: firstArray(cvData?.education, cvData?.educationHistory, cvData?.academicHistory).map((item) => ({
      qualification: item.qualification || item.degree || item.title || item.program || '',
      institution: item.institution || item.school || item.university || '',
      year: item.year || item.end_year || item.endDate || item.graduationYear || ''
    })),
    credentials: normalizeArray(cvData?.credentials || cvData?.qualifications || cvData?.certifications || cvData?.licenses),
    languages: normalizeArray(cvData?.languages || cvData?.languageSkills),
    systems: normalizeArray(cvData?.systems || cvData?.tools || cvData?.platforms),
    commercialInformation: cvData?.commercialInformation || compensationInformation,
    commercialApproved: Boolean(cvData?.commercialApproved),
    confidentialityText: cvData?.confidentialityText || EXAMPLE_PROFILE.confidentialityText,
    experienceSource: workExperience.length ? 'seeded' : 'fallback',
    experienceApproved: false,
    experienceApprovedAt: null
  };
};

const getDisplayName = (profile) => {
  if (profile.identityMode === 'anonymous') {
    return profile.anonymousLabel || 'Candidate A';
  }
  return profile.displayName || profile.anonymousLabel || 'Candidate A';
};

const getWarnings = (profile) => {
  const warnings = [];
  if (!profile.targetRole) warnings.push('Target role is missing.');
  if (!profile.assessment || profile.assessment.length < 40) warnings.push('Assessment is too short to be useful.');
  if (!profile.confidentialityText) warnings.push('Confidentiality footer is missing.');
  if (profile.identityMode === 'anonymous' && profile.portraitUrl) warnings.push('Portrait URL is stored but will not render in anonymous mode.');
  if (profile.identityMode === 'named_with_portrait' && (!profile.portraitConsent || !profile.portraitUrl)) {
    warnings.push('Portrait mode needs both portrait consent and a portrait URL.');
  }
  if (!profile.whyRelevant || profile.whyRelevant.length < 40) warnings.push('Explain why this candidate is relevant.');
  if (normalizeArray(profile.keySkills).length > 8) warnings.push('Only the first 8 key skills will be included.');
  if (normalizeArray(profile.experience).length === 0) warnings.push('No experience rows are present.');
  if (normalizeArray(profile.experience).length > 0 && !profile.experienceApproved) warnings.push('Work experience has not been approved.');
  return warnings;
};

const getErrors = (profile) => {
  const errors = [];
  ['profileVersion', 'generatedAt', 'identityMode', 'candidateId', 'targetRole', 'assessment', 'confidentialityText'].forEach((key) => {
    if (profile[key] === undefined || profile[key] === null || profile[key] === '') {
      errors.push(`Missing required field: ${key}`);
    }
  });
  if (!['anonymous', 'named_without_portrait', 'named_with_portrait'].includes(profile.identityMode)) {
    errors.push('identityMode must be anonymous, named_without_portrait, or named_with_portrait.');
  }
  return errors;
};

const chunk = (arr, size) => {
  const list = normalizeArray(arr);
  if (!list.length) return [];
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

const getAuthToken = async () => {
  if (auth.currentUser) return auth.currentUser.getIdToken();
  return localStorage.getItem('token');
};

const CandidateIntelligenceProfileBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialCvData = location.state?.cvData || null;
  const backCandidateId = location.state?.backCandidateId || initialCvData?.id || initialCvData?.contact?.id || '';

  const [profile, setProfile] = useState(() => initialCvData ? mapCvDataToProfile(initialCvData) : EXAMPLE_PROFILE);
  const [jsonText, setJsonText] = useState(() => JSON.stringify(initialCvData ? mapCvDataToProfile(initialCvData) : EXAMPLE_PROFILE, null, 2));
  const [selectedTab, setSelectedTab] = useState('editor');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [reparseLoading, setReparseLoading] = useState(false);
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [status, setStatus] = useState({ type: 'info', text: 'Edit the profile, then validate and export.' });
  const [experienceBackfilled, setExperienceBackfilled] = useState(false);

  useEffect(() => {
    if (initialCvData) {
      const seeded = mapCvDataToProfile(initialCvData);
      setProfile(seeded);
      setJsonText(JSON.stringify(seeded, null, 2));
      setStatus({ type: 'success', text: 'Profile seeded from current CV data.' });
    }
  }, [initialCvData]);

  useEffect(() => {
    if (!initialCvData || experienceBackfilled || profile.experienceSource !== 'fallback' || profile.experienceApproved) return;

    const candidateId = backCandidateId || initialCvData?.id || initialCvData?.candidate_id || '';
    const fullText = initialCvData?.fullText || initialCvData?.full_text || initialCvData?.text || '';
    if (!candidateId || !fullText.trim()) return;

    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token || cancelled) return;
        setStatus({ type: 'info', text: 'Backfilling work experience from resume text…' });
        const resp = await axios.post(
          `/api/candidates/${encodeURIComponent(candidateId)}/extract-profile-from-resume`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const parsedRoles = normalizeArray(resp?.data?.workExperience).map((item, index) => ({
          title: item?.title || item?.role || `Role ${index + 1}`,
          organisation: item?.company || item?.organisation || '',
          anonymousOrganisation: item?.anonymousOrganisation || item?.company || 'Organisation withheld',
          company: item?.company || item?.organisation || '',
          startDate: item?.years || item?.startDate || '',
          endDate: item?.endDate || 'Present',
          remit: item?.remit || '',
          bullets: toTextArray(item?.bullets || item?.description || item?.summary || [])
        }));

        if (!cancelled && parsedRoles.length > 0) {
          setProfile((currentProfile) => {
            const nextProfile = {
              ...currentProfile,
              targetRole: resp?.data?.job_title || currentProfile.targetRole,
              experience: parsedRoles,
              experienceSource: 'extracted',
              profileStatus: 'draft',
              profileApproved: false,
              profileApprovedAt: null,
              experienceApproved: false,
              experienceApprovedAt: null
            };
            setJsonText(JSON.stringify(nextProfile, null, 2));
            return nextProfile;
          });
          setExperienceBackfilled(true);
          setStatus({ type: 'success', text: 'Work experience backfilled from resume text.' });
        } else if (!cancelled) {
          setExperienceBackfilled(true);
          setStatus({ type: 'warning', text: 'No structured work experience could be extracted from the resume text.' });
        }
      } catch (error) {
        if (!cancelled) {
          setExperienceBackfilled(true);
          setStatus({ type: 'warning', text: 'Could not auto-parse work experience from resume text.' });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [backCandidateId, experienceBackfilled, initialCvData, profile.experienceApproved, profile.experienceSource]);

  const warnings = useMemo(() => getWarnings(profile), [profile]);
  const errors = useMemo(() => getErrors(profile), [profile]);
  const displayName = getDisplayName(profile);
  const displayedKeySkills = useMemo(() => {
    const skills = Array.isArray(profile.keySkills)
      ? normalizeArray(profile.keySkills)
      : normalizeArray(profile.marketFootprint).map((item) => (
        typeof item === 'string' ? item : item?.label || item?.name || item?.title
      )).filter(Boolean);
    return skills.slice(0, 8);
  }, [profile.keySkills, profile.marketFootprint]);
  const experienceGroups = useMemo(() => {
    const groups = chunk(profile.experience, 3);
    const hasSupportingDetails = [profile.education, profile.credentials, profile.languages, profile.systems]
      .some((items) => normalizeArray(items).length > 0);
    return groups.length || !hasSupportingDetails ? groups : [[]];
  }, [profile.credentials, profile.education, profile.experience, profile.languages, profile.systems]);
  const pageCount = 1 + experienceGroups.length;

  const syncProfile = (nextProfile) => {
    const explicitlyApproving = nextProfile.profileApproved === true;
    const merged = {
      ...profile,
      ...nextProfile,
      profileStatus: explicitlyApproving ? 'approved' : 'draft',
      profileApproved: explicitlyApproving,
      profileApprovedAt: explicitlyApproving ? nextProfile.profileApprovedAt : null,
      generatedAt: nextProfile.generatedAt || profile.generatedAt || new Date().toISOString()
    };
    setProfile(merged);
    setJsonText(JSON.stringify(merged, null, 2));
  };

  const updateExperience = (index, changes) => {
    const experience = normalizeArray(profile.experience).map((entry, entryIndex) => (
      entryIndex === index ? { ...EMPTY_EXPERIENCE, ...entry, ...changes } : entry
    ));
    syncProfile({
      experience,
      experienceSource: 'manual',
      experienceApproved: false,
      experienceApprovedAt: null
    });
  };

  const addExperience = () => {
    syncProfile({
      experience: [...normalizeArray(profile.experience), { ...EMPTY_EXPERIENCE }],
      experienceSource: 'manual',
      experienceApproved: false,
      experienceApprovedAt: null
    });
  };

  const removeExperience = (index) => {
    syncProfile({
      experience: normalizeArray(profile.experience).filter((_, entryIndex) => entryIndex !== index),
      experienceSource: 'manual',
      experienceApproved: false,
      experienceApprovedAt: null
    });
  };

  const approveExperience = () => {
    const experience = normalizeArray(profile.experience);
    if (!experience.length) {
      message.warning('Add or extract at least one role before approving experience.');
      return;
    }
    const incompleteRole = experience.find((entry) => (
      !String(entry?.title || '').trim() ||
      !String(entry?.organisation || entry?.anonymousOrganisation || '').trim()
    ));
    if (incompleteRole) {
      message.warning('Every role needs a job title and organisation before approval.');
      return;
    }
    const approvedAt = new Date().toISOString();
    syncProfile({ experienceApproved: true, experienceApprovedAt: approvedAt });
    setStatus({ type: 'success', text: 'Work experience approved for the final profile.' });
    message.success('Work experience approved.');
  };

  const approveFinalProfile = () => {
    if (normalizeArray(profile.experience).length > 0 && !profile.experienceApproved) {
      message.warning('Approve work experience before approving the final profile.');
      return;
    }
    if (errors.length > 0) {
      message.error('Resolve the profile validation errors before final approval.');
      return;
    }
    const approvedAt = new Date().toISOString();
    syncProfile({
      profileApproved: true,
      profileStatus: 'approved',
      profileApprovedAt: approvedAt
    });
    setStatus({ type: 'success', text: 'Final candidate profile approved by the user.' });
    message.success('Final candidate profile approved.');
  };

  const updateExperienceBullet = (experienceIndex, bulletIndex, value) => {
    const entry = normalizeArray(profile.experience)[experienceIndex] || EMPTY_EXPERIENCE;
    const bullets = normalizeArray(entry.bullets).map((bullet, index) => (
      index === bulletIndex ? value.replace(/^[\s\u2022*-]+/, '') : bullet
    ));
    updateExperience(experienceIndex, { bullets });
  };

  const addExperienceBullet = (experienceIndex) => {
    const entry = normalizeArray(profile.experience)[experienceIndex] || EMPTY_EXPERIENCE;
    updateExperience(experienceIndex, { bullets: [...normalizeArray(entry.bullets), ''] });
  };

  const removeExperienceBullet = (experienceIndex, bulletIndex) => {
    const entry = normalizeArray(profile.experience)[experienceIndex] || EMPTY_EXPERIENCE;
    updateExperience(experienceIndex, {
      bullets: normalizeArray(entry.bullets).filter((_, index) => index !== bulletIndex)
    });
  };

  const updateEducation = (index, changes) => {
    const education = normalizeArray(profile.education).map((entry, entryIndex) => (
      entryIndex === index ? { ...EMPTY_EDUCATION, ...entry, ...changes } : entry
    ));
    syncProfile({ education });
  };

  const addEducation = () => {
    syncProfile({ education: [...normalizeArray(profile.education), { ...EMPTY_EDUCATION }] });
  };

  const removeEducation = (index) => {
    syncProfile({ education: normalizeArray(profile.education).filter((_, entryIndex) => entryIndex !== index) });
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const merged = {
        ...profile,
        ...parsed,
        profileStatus: 'draft',
        profileApproved: false,
        profileApprovedAt: null
      };
      setProfile(merged);
      setJsonText(JSON.stringify(merged, null, 2));
      setStatus({ type: 'success', text: 'Profile JSON applied successfully.' });
    } catch (e) {
      setStatus({ type: 'error', text: `Invalid JSON: ${e.message}` });
    }
  };

  const resetExample = () => {
    const exampleDraft = {
      ...EXAMPLE_PROFILE,
      profileStatus: 'draft',
      profileApproved: false,
      profileApprovedAt: null
    };
    setProfile(exampleDraft);
    setJsonText(JSON.stringify(exampleDraft, null, 2));
    setStatus({ type: 'info', text: 'Example profile loaded.' });
  };

  const loadFile = async (file) => {
    const text = await file.text();
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setProfile((prev) => ({
        ...prev,
        ...parsed,
        profileStatus: 'draft',
        profileApproved: false,
        profileApprovedAt: null
      }));
      setStatus({ type: 'success', text: 'JSON imported successfully.' });
    } catch (e) {
      setStatus({ type: 'error', text: `Imported file is not valid JSON: ${e.message}` });
    }
    return false;
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(profile, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidate-profile-${profile.candidateId || 'A'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reparseWorkExperience = async () => {
    const candidateId = backCandidateId || initialCvData?.id || initialCvData?.candidate_id || '';
    const fullText = initialCvData?.fullText || initialCvData?.full_text || initialCvData?.text || '';
    if (!candidateId) {
      message.warning('No candidate ID available to extract work experience.');
      return;
    }
    if (profile.experienceApproved && !window.confirm('This experience is approved. Extracting again will replace it and require a new approval. Continue?')) {
      return;
    }

    const token = await getAuthToken();
    if (!token) {
      message.warning('You must be logged in to extract work experience.');
      return;
    }

    try {
      setReparseLoading(true);
      setStatus({ type: 'info', text: 'Extracting work experience from the resume...' });
      const candidateResp = await axios.get(
        `/api/candidates/${encodeURIComponent(candidateId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const candidateRecord = candidateResp?.data?.candidate || {};
      const candidateStructuredExperience = normalizeArray(
        candidateRecord.workExperience ||
        candidateRecord.work_experience ||
        candidateRecord.professionalExperience ||
        candidateRecord.experience
      ).map((item, index) => normalizeExperienceEntry(item, index));

      if (candidateStructuredExperience.length > 0) {
        const nextProfile = {
          ...profile,
          candidateId: candidateRecord.id || profile.candidateId,
          anonymousLabel: candidateRecord.anonymousLabel || profile.anonymousLabel || 'Candidate A',
          displayName: candidateRecord.contactName || candidateRecord.displayName || profile.displayName,
          targetRole: candidateRecord.job_title || candidateRecord.title || profile.targetRole,
          experience: candidateStructuredExperience,
          experienceSource: 'extracted',
          profileStatus: 'draft',
          profileApproved: false,
          profileApprovedAt: null,
          experienceApproved: false,
          experienceApprovedAt: null
        };

        setProfile(nextProfile);
        setJsonText(JSON.stringify(nextProfile, null, 2));
        setExperienceBackfilled(true);
        setStatus({ type: 'success', text: 'Work experience extracted. Review and approve it before export.' });
        message.success('Work experience extracted from the candidate record.');
        return;
      }

      const extractedText = candidateRecord.fullText || candidateRecord.full_text || candidateRecord.text || fullText;
      if (!extractedText.trim()) {
        setStatus({ type: 'warning', text: 'No structured work experience or resume text could be found for this candidate.' });
        message.warning('No structured work experience or resume text could be found for this candidate.');
        return;
      }

      const resp = await axios.post(
        `/api/candidates/${encodeURIComponent(candidateId)}/extract-profile-from-resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const parsedRoles = normalizeArray(resp?.data?.workExperience).map((item, index) => normalizeExperienceEntry({
        ...item,
        description: item?.description || item?.bullets || item?.summary || [],
        descriptionText: item?.descriptionText || item?.description_text || '',
      }, index));

      if (!parsedRoles.length) {
        setStatus({ type: 'warning', text: 'No structured work experience could be extracted from the resume text.' });
        return;
      }

      const nextProfile = {
        ...profile,
        targetRole: resp?.data?.job_title || profile.targetRole,
        experience: parsedRoles,
        experienceSource: 'extracted',
        profileStatus: 'draft',
        profileApproved: false,
        profileApprovedAt: null,
        experienceApproved: false,
        experienceApprovedAt: null
      };

      setProfile(nextProfile);
      setJsonText(JSON.stringify(nextProfile, null, 2));
      setExperienceBackfilled(true);
      setStatus({ type: 'success', text: 'Work experience extracted. Review and approve it before export.' });
      message.success('Work experience extracted successfully.');
    } catch (error) {
      console.error('Error extracting work experience:', error);
      setStatus({ type: 'warning', text: error?.response?.data?.error || 'Could not extract work experience.' });
      message.error(error?.response?.data?.error || 'Could not extract work experience.');
    } finally {
      setReparseLoading(false);
    }
  };

  const prepareWithAlice = async () => {
    const candidateId = backCandidateId || profile.candidateId || '';
    if (!candidateId || candidateId === 'A') {
      message.warning('Open the builder from a candidate record before preparing a profile with ALICE.');
      return;
    }
    const token = await getAuthToken();
    if (!token) {
      message.warning('You must be logged in to prepare a candidate profile.');
      return;
    }
    if (!window.confirm('ALICE will replace the current builder content with a new unapproved draft. Continue?')) {
      return;
    }

    try {
      setPrepareLoading(true);
      setStatus({ type: 'info', text: 'ALICE is preparing an editable profile draft from the full resume...' });
      const jobId = initialCvData?.lastEvaluatedJob?.id || initialCvData?.contact?.lastEvaluatedJob?.id || null;
      const response = await axios.post(
        `/api/candidates/${encodeURIComponent(candidateId)}/prepare-profile-draft`,
        jobId ? { job_id: jobId } : {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!response?.data?.success || !response?.data?.profile) {
        throw new Error(response?.data?.error || 'ALICE did not return a profile draft.');
      }

      const prepared = {
        ...EXAMPLE_PROFILE,
        ...response.data.profile,
        profileStatus: 'draft',
        profileApproved: false,
        profileApprovedAt: null,
        experienceApproved: false,
        experienceApprovedAt: null
      };
      setProfile(prepared);
      setJsonText(JSON.stringify(prepared, null, 2));
      setGeneratedPdf(null);
      setStatus({ type: 'warning', text: 'ALICE draft prepared. Review, edit, and approve it before generating or sending the final PDF.' });
      message.success('ALICE prepared an editable profile draft.');
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Could not prepare candidate profile.';
      setStatus({ type: 'error', text: errorMessage });
      message.error(errorMessage);
    } finally {
      setPrepareLoading(false);
    }
  };

  const validatePdfGeneration = () => {
    if (normalizeArray(profile.experience).length > 0 && !profile.experienceApproved) {
      message.warning('Review and approve work experience before generating the final PDF.');
      return false;
    }
    if (!profile.profileApproved) {
      message.warning('Approve the final candidate profile before generating or sending the PDF.');
      return false;
    }
    return true;
  };

  const generateProfilePdf = async () => {
    if (!validatePdfGeneration()) return null;

    const profileSnapshot = JSON.stringify(profile);
    if (generatedPdf?.profileSnapshot === profileSnapshot) return generatedPdf;

    try {
      setPdfLoading(true);
      const resp = await contactsApi.formatCandidateProfile({
        profile,
        format: 'html-pdf'
      });
      if (resp?.success && resp?.pdfB64) {
        const fileBase = profile.identityMode === 'anonymous'
          ? 'Confidential-Candidate_Profile'
          : `${displayName.replace(/\s+/g, '_')}_Candidate_Profile`;
        const result = {
          pdfB64: resp.pdfB64,
          filename: `${fileBase}.pdf`,
          profileSnapshot
        };
        setGeneratedPdf(result);
        return result;
      } else {
        message.error(resp?.error || 'Failed to generate profile PDF');
      }
    } catch (e) {
      message.error(e?.message || 'Failed to generate profile PDF');
    } finally {
      setPdfLoading(false);
    }
    return null;
  };

  const exportPdf = async () => {
    const result = await generateProfilePdf();
    if (!result) return;

    const a = document.createElement('a');
    a.href = `data:application/pdf;base64,${result.pdfB64}`;
    a.download = result.filename;
    a.click();
    message.success('Candidate profile PDF generated');
  };

  const previewGeneratedPdf = async () => {
    const previewWindow = window.open('', '_blank');
    const result = await generateProfilePdf();
    if (!result) {
      previewWindow?.close();
      return;
    }

    const byteCharacters = window.atob(result.pdfB64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let index = 0; index < byteCharacters.length; index += 1) {
      byteNumbers[index] = byteCharacters.charCodeAt(index);
    }
    const blobUrl = URL.createObjectURL(new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' }));
    if (previewWindow) {
      previewWindow.location.href = blobUrl;
    } else {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    }
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  const openSendToClient = async () => {
    const candidateId = backCandidateId || profile.candidateId || '';
    if (!candidateId || candidateId === 'A') {
      message.warning('Open the builder from a candidate record before sending this profile to a client.');
      return;
    }

    const result = await generateProfilePdf();
    if (!result) return;
    setSendModalOpen(true);
  };

  const heroName = getDisplayName(profile);
  const returnCandidateId = backCandidateId || profile.candidateId || '';

  return (
    <div className="cipb-shell">
      <aside className="cipb-editor">
        <div className="cipb-editor__head">
          <div>
            <div className="cipb-eyebrow">Candid-I production tool</div>
            <h1>Candidate Profile Builder</h1>
          </div>
          <Button type="text" icon={<ReloadOutlined />} onClick={() => navigate('/candidate-search')} />
        </div>

        <Space wrap className="cipb-actions">
          <Button icon={<RobotOutlined />} loading={prepareLoading} onClick={prepareWithAlice}>
            Prepare with ALICE
          </Button>
          <Upload beforeUpload={loadFile} showUploadList={false} accept=".json,application/json">
            <Button icon={<ImportOutlined />}>Import JSON</Button>
          </Upload>
          <Button icon={<SaveOutlined />} onClick={exportJson}>Export JSON</Button>
          <Button
            icon={<CheckCircleOutlined />}
            type={profile.profileApproved ? 'default' : 'primary'}
            disabled={Boolean(profile.profileApproved)}
            onClick={approveFinalProfile}
          >
            {profile.profileApproved ? 'Final profile approved' : 'Approve final profile'}
          </Button>
          <Button icon={<EyeOutlined />} loading={pdfLoading} onClick={previewGeneratedPdf}>
            Preview PDF
          </Button>
          <Button icon={<DownloadOutlined />} loading={pdfLoading} onClick={exportPdf}>
            Print / Save PDF
          </Button>
          <Button icon={<MailOutlined />} loading={pdfLoading} onClick={openSendToClient}>
            Send to client
          </Button>
          <Tag color={profile.profileApproved ? 'green' : 'orange'}>
            {profile.profileApproved ? 'APPROVED' : 'DRAFT - REVIEW REQUIRED'}
          </Tag>
        </Space>

        <Alert
          type={status.type}
          message={status.text}
          showIcon
          style={{ marginBottom: 14 }}
        />

        <Card size="small" title="Identity mode" className="cipb-card">
          <Radio.Group
            value={profile.identityMode}
            onChange={(e) => syncProfile({ identityMode: e.target.value })}
          >
            <Space direction="vertical">
              <Radio value="anonymous">Anonymous</Radio>
              <Radio value="named_without_portrait">Named, no portrait</Radio>
              <Radio value="named_with_portrait">Named + portrait</Radio>
            </Space>
          </Radio.Group>
          <Divider style={{ margin: '12px 0' }} />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Checkbox
              checked={profile.portraitConsent}
              onChange={(e) => syncProfile({ portraitConsent: e.target.checked })}
            >
              Portrait consent
            </Checkbox>
            <Checkbox
              checked={profile.commercialApproved}
              onChange={(e) => syncProfile({ commercialApproved: e.target.checked })}
            >
              Commercial information approved
            </Checkbox>
            <Checkbox
              checked={profile.lowInkPrint}
              onChange={(e) => syncProfile({ lowInkPrint: e.target.checked })}
            >
              Low-ink print mode
            </Checkbox>
          </Space>
        </Card>

        <Card size="small" title="Core fields" className="cipb-card">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              value={profile.targetRole}
              onChange={(e) => syncProfile({ targetRole: e.target.value })}
              placeholder="Target role"
            />
            <Input
              value={profile.descriptor}
              onChange={(e) => syncProfile({ descriptor: e.target.value })}
              placeholder="Descriptor"
            />
            <Input
              value={profile.market}
              onChange={(e) => syncProfile({ market: e.target.value })}
              placeholder="Market"
            />
            <Input
              value={profile.mobility}
              onChange={(e) => syncProfile({ mobility: e.target.value })}
              placeholder="Mobility"
            />
            <Input
              value={profile.availability}
              onChange={(e) => syncProfile({ availability: e.target.value })}
              placeholder="Availability"
            />
            <Input
              value={profile.portraitUrl}
              onChange={(e) => syncProfile({ portraitUrl: e.target.value })}
              placeholder="Portrait URL (optional)"
            />
            <Input
              value={profile.portraitAlt}
              onChange={(e) => syncProfile({ portraitAlt: e.target.value })}
              placeholder="Portrait alt text"
            />
            <Select
              value={profile.fitSignal}
              onChange={(value) => syncProfile({ fitSignal: value })}
              options={[
                { value: 'strong', label: 'Strong relevance' },
                { value: 'validate', label: 'Relevant with points to validate' },
                { value: 'exploratory', label: 'Exploratory relevance' }
              ]}
            />
            <TextArea
              rows={4}
              value={profile.assessment}
              onChange={(e) => syncProfile({ assessment: e.target.value })}
              placeholder="Assessment"
            />
            <label className="cipb-field">
              <span>Why this candidate is relevant</span>
              <TextArea
                rows={5}
                value={profile.whyRelevant || ''}
                onChange={(e) => syncProfile({ whyRelevant: e.target.value })}
                placeholder="Explain the evidence connecting this candidate to the target role."
              />
            </label>
            <label className="cipb-field">
              <span>Key Skills</span>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                maxCount={8}
                maxTagCount="responsive"
                value={displayedKeySkills}
                onChange={(value) => {
                  if (value.length > 8) {
                    message.warning('A maximum of 8 key skills can be included.');
                    return;
                  }
                  syncProfile({ keySkills: value });
                }}
                placeholder="Type a skill and press Enter"
                tokenSeparators={[',']}
              />
              <small>{displayedKeySkills.length}/8 skills. Type a skill and press Enter; commas also create separate skills.</small>
            </label>
            <TextArea
              rows={2}
              value={profile.confidentialityText}
              onChange={(e) => syncProfile({ confidentialityText: e.target.value })}
              placeholder="Confidentiality footer"
            />
          </Space>
        </Card>

        <Card
          size="small"
          title="Work experience"
          className="cipb-card"
          extra={<Button size="small" icon={<PlusOutlined />} onClick={addExperience}>Add role</Button>}
        >
          <Paragraph className="cipb-field-help">
            Add each role below. These fields automatically update the profile JSON and PDF preview.
          </Paragraph>
          <div className="cipb-experience-controls">
            <Tag color={profile.experienceApproved ? 'green' : 'gold'}>
              {profile.experienceApproved ? 'Approved' : 'Review required'}
            </Tag>
            <Space wrap>
              <Button loading={reparseLoading} onClick={reparseWorkExperience}>
                Extract work experience
              </Button>
              <Button
                type={profile.experienceApproved ? 'default' : 'primary'}
                icon={<CheckCircleOutlined />}
                onClick={approveExperience}
              >
                Approve experience
              </Button>
            </Space>
          </div>
          {profile.experienceApprovedAt ? (
            <div className="cipb-approved-at">
              Approved {new Date(profile.experienceApprovedAt).toLocaleString()}
            </div>
          ) : null}
          {normalizeArray(profile.experience).length === 0 && (
            <div className="cipb-experience-empty">
              No roles added yet.
              <Button type="link" onClick={addExperience}>Add the first role</Button>
            </div>
          )}
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {normalizeArray(profile.experience).map((entry, index) => (
              <section className="cipb-experience-editor" key={index}>
                <div className="cipb-experience-editor__head">
                  <strong>Role {index + 1}</strong>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeExperience(index)}
                    aria-label={`Remove role ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
                <label className="cipb-field">
                  <span>Job title</span>
                  <Input
                    value={entry.title || ''}
                    onChange={(e) => updateExperience(index, { title: e.target.value })}
                    placeholder="Regional Commercial Director"
                  />
                </label>
                <label className="cipb-field">
                  <span>Organisation</span>
                  <Input
                    value={entry.organisation || ''}
                    onChange={(e) => updateExperience(index, { organisation: e.target.value })}
                    placeholder="Actual organisation name"
                  />
                </label>
                <label className="cipb-field">
                  <span>Anonymous organisation</span>
                  <Input
                    value={entry.anonymousOrganisation || ''}
                    onChange={(e) => updateExperience(index, { anonymousOrganisation: e.target.value })}
                    placeholder="Global Precious Metals Group"
                  />
                </label>
                <div className="cipb-date-grid">
                  <label className="cipb-field">
                    <span>Start date</span>
                    <Input
                      value={entry.startDate || ''}
                      onChange={(e) => updateExperience(index, { startDate: e.target.value })}
                      placeholder="2019"
                    />
                  </label>
                  <label className="cipb-field">
                    <span>End date</span>
                    <Input
                      value={entry.endDate || ''}
                      onChange={(e) => updateExperience(index, { endDate: e.target.value })}
                      placeholder="Present"
                    />
                  </label>
                </div>
                <label className="cipb-field">
                  <span>Role remit</span>
                  <TextArea
                    rows={3}
                    value={entry.remit || ''}
                    onChange={(e) => updateExperience(index, { remit: e.target.value })}
                    placeholder="Summarise the scope, market, team, and purpose of the role."
                  />
                </label>
                <label className="cipb-field">
                  <span>Responsibilities and achievements</span>
                  <small>Each box becomes one bullet in the JSON and rendered resume.</small>
                </label>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {normalizeArray(entry.bullets).map((bullet, bulletIndex) => (
                    <div className="cipb-bullet-editor" key={bulletIndex}>
                      <TextArea
                        autoSize={{ minRows: 2, maxRows: 7 }}
                        value={bullet}
                        onChange={(e) => updateExperienceBullet(index, bulletIndex, e.target.value)}
                        placeholder="Describe an achievement, responsibility, project, or measurable outcome."
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeExperienceBullet(index, bulletIndex)}
                        aria-label={`Remove achievement ${bulletIndex + 1}`}
                      />
                    </div>
                  ))}
                  <Button size="small" icon={<PlusOutlined />} onClick={() => addExperienceBullet(index)}>
                    Add achievement
                  </Button>
                </Space>
              </section>
            ))}
          </Space>
        </Card>

        <Card
          size="small"
          title="Education"
          className="cipb-card"
          extra={<Button size="small" icon={<PlusOutlined />} onClick={addEducation}>Add education</Button>}
        >
          <Paragraph className="cipb-field-help">
            Add qualifications, institutions, and completion years as they should appear in the final profile.
          </Paragraph>
          {normalizeArray(profile.education).length === 0 && (
            <div className="cipb-experience-empty">
              No education added yet.
              <Button type="link" onClick={addEducation}>Add the first qualification</Button>
            </div>
          )}
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {normalizeArray(profile.education).map((entry, index) => (
              <section className="cipb-experience-editor" key={index}>
                <div className="cipb-experience-editor__head">
                  <strong>Education {index + 1}</strong>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeEducation(index)}
                    aria-label={`Remove education ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
                <label className="cipb-field">
                  <span>Qualification</span>
                  <Input
                    value={entry.qualification ?? entry.degree ?? ''}
                    onChange={(e) => updateEducation(index, { qualification: e.target.value })}
                    placeholder="Bachelor of Chemical Engineering"
                  />
                </label>
                <label className="cipb-field">
                  <span>Institution</span>
                  <Input
                    value={entry.institution ?? entry.school ?? entry.university ?? ''}
                    onChange={(e) => updateEducation(index, { institution: e.target.value })}
                    placeholder="National University of Singapore"
                  />
                </label>
                <label className="cipb-field">
                  <span>Completion year</span>
                  <Input
                    value={entry.year || ''}
                    onChange={(e) => updateEducation(index, { year: e.target.value })}
                    placeholder="2020"
                  />
                </label>
              </section>
            ))}
          </Space>
        </Card>

        <Card size="small" title="Advanced: Structured JSON" className="cipb-card">
          <TextArea
            rows={24}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            spellCheck={false}
            className="cipb-json"
          />
          <Space wrap style={{ marginTop: 12 }}>
            <Button onClick={applyJson}>Validate & apply</Button>
            <Button onClick={resetExample}>Load example</Button>
          </Space>
        </Card>

        {warnings.length > 0 && (
          <Card size="small" title="Quality warnings" className="cipb-card">
            <ul className="cipb-list">
              {warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          </Card>
        )}
        {errors.length > 0 && (
          <Card size="small" title="Schema errors" className="cipb-card">
            <ul className="cipb-list cipb-list--error">
              {errors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          </Card>
        )}

        <Card size="small" title="Quick notes" className="cipb-card">
          <Paragraph style={{ marginBottom: 8 }}>
            This builder generates an A4 print-safe profile that the email agent can attach after PDF generation.
          </Paragraph>
          <Tag color="blue">PDF ready</Tag>
          <Tag color="gold">Anonymous by default</Tag>
          <Tag color="green">Email-agent compatible</Tag>
        </Card>
      </aside>

      <main className="cipb-preview" aria-label="Candidate profile preview">
        <div className="cipb-preview__tabs">
          <Button type={selectedTab === 'editor' ? 'primary' : 'default'} onClick={() => setSelectedTab('editor')} icon={<FileTextOutlined />}>Builder</Button>
          <Button type={selectedTab === 'preview' ? 'primary' : 'default'} onClick={() => setSelectedTab('preview')} icon={<EyeOutlined />}>Preview</Button>
        </div>

        <div className="cipb-page">
          <section className="cipb-hero">
            <div className="cipb-badge">Confidential candidate submission</div>
            <div className="cipb-hero__grid">
                <div className="cipb-hero__main">
                  <div className="cipb-brand">
                    <img src="/ci-logo.png" alt="Candid-I" />
                  </div>
                  <div className="cipb-eyebrow">Candidate Intelligence Profile</div>
                  <h1>{heroName}</h1>
                <p className="cipb-role">Role Applied For: {profile.targetRole}</p>
                {profile.descriptor ? <p className="cipb-descriptor">{profile.descriptor}</p> : null}
                <dl className="cipb-meta">
                  {profile.market ? <div><dt>Market</dt><dd>{profile.market}</dd></div> : null}
                  {profile.yearsExperience !== undefined && profile.yearsExperience !== null ? <div><dt>Experience</dt><dd>{profile.yearsExperience} years</dd></div> : null}
                  {profile.mobility ? <div><dt>Mobility</dt><dd>{profile.mobility}</dd></div> : null}
                  {profile.availability ? <div><dt>Availability</dt><dd>{profile.availability}</dd></div> : null}
                </dl>
                <div className="cipb-fit">
                  <div className="cipb-fit__label">Fit signal</div>
                  <div className="cipb-fit__value">{profile.fitSignal}</div>
                </div>
              </div>
              {profile.identityMode === 'named_with_portrait' && profile.portraitConsent && profile.portraitUrl ? (
                <figure className="cipb-portrait">
                  <img src={profile.portraitUrl} alt={profile.portraitAlt || heroName} />
                </figure>
              ) : null}
            </div>
          </section>

          <div className="cipb-page__inner">
            <section className="cipb-section">
              <h2 className="cipb-heading">Candidate assessment</h2>
              <p className="cipb-assessment">{profile.assessment}</p>
            </section>

            <section className="cipb-section">
              <h2 className="cipb-heading">Why this candidate is relevant</h2>
              <p className="cipb-assessment">{profile.whyRelevant || normalizeArray(profile.mandateFit).join(' ')}</p>
            </section>

            <section className="cipb-section">
              <h2 className="cipb-heading">Key Skills</h2>
              <div className="cipb-footprints">
                {displayedKeySkills.map((item, idx) => (
                  <div className="cipb-footprint" key={`${idx}-${typeof item === 'string' ? item : item.label || item.title}`}>
                    <div className="cipb-footprint__title">
                      {typeof item === 'string' ? item : item.label || item.title || 'Footprint'}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {experienceGroups.map((group, index) => (
          <div className="cipb-page" key={`group-${index}`}>
            <header className="cipb-page__header">
              <div className="cipb-brand">
                <img src="/ci-logo.png" alt="Candid-I" />
              </div>
              <h2>Career evidence</h2>
              <p>{heroName} · {profile.targetRole}</p>
            </header>
            <div className="cipb-page__inner">
              {group.map((role, roleIndex) => (
                <article className="cipb-role" key={`${roleIndex}-${role.title || role.role || 'role'}`}>
                  <div className="cipb-role__top">
                    <div>
                      <h3>{role.title || role.role || role.position || 'Role'}</h3>
                      <p className="cipb-role__org">
                        {profile.identityMode === 'anonymous'
                          ? (role.anonymousOrganisation || 'Organisation withheld')
                          : (role.organisation || role.company || role.anonymousOrganisation || 'Organisation')}
                      </p>
                    </div>
                    <div className="cipb-role__dates">{role.startDate || role.start || ''} - {role.endDate || role.end || 'Present'}</div>
                  </div>
                  {role.remit ? <p className="cipb-role__remit">{role.remit}</p> : null}
                  {normalizeArray(role.bullets).length > 0 ? (
                    <ul className="cipb-role__bullets">
                      {normalizeArray(role.bullets).map((bullet, bulletIdx) => <li key={`${bulletIdx}-${bullet}`}>{bullet}</li>)}
                    </ul>
                  ) : null}
                </article>
              ))}

              {index === experienceGroups.length - 1 && (
                <div className="cipb-support">
                  {normalizeArray(profile.education).length > 0 && (
                    <section className="cipb-support__block">
                      <h3>Education</h3>
                      <ul>
                        {normalizeArray(profile.education).map((item, idx) => (
                          <li key={`${idx}-${item.institution || item.qualification || 'edu'}`}>
                            {item.qualification || item.degree || ''}{item.institution ? ` · ${item.institution}` : ''}{item.year ? ` · ${item.year}` : ''}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {normalizeArray(profile.credentials).length > 0 && (
                    <section className="cipb-support__block">
                      <h3>Credentials</h3>
                      <ul>{profile.credentials.map((item) => <li key={item}>{item}</li>)}</ul>
                    </section>
                  )}
                  {normalizeArray(profile.languages).length > 0 && (
                    <section className="cipb-support__block">
                      <h3>Languages</h3>
                      <div className="cipb-tags">
                        {profile.languages.map((item) => <Tag key={item}>{item}</Tag>)}
                      </div>
                    </section>
                  )}
                  {normalizeArray(profile.systems).length > 0 && (
                    <section className="cipb-support__block">
                      <h3>Systems</h3>
                      <div className="cipb-tags">
                        {profile.systems.map((item) => <Tag key={item}>{item}</Tag>)}
                      </div>
                    </section>
                  )}
                  {profile.commercialApproved && profile.commercialInformation ? (
                    <section className="cipb-support__block cipb-support__block--commercial">
                      <h3>Commercial information</h3>
                      <p>{profile.commercialInformation}</p>
                    </section>
                  ) : null}
                  <section className="cipb-support__block cipb-support__block--journey">
                    <h3>Candidate journey</h3>
                    <ol>
                      <li>Initial profile review</li>
                      <li>Recruiter validation</li>
                      <li>Client presentation</li>
                      <li>Interview and feedback tracking</li>
                    </ol>
                  </section>
                </div>
              )}
            </div>
            <footer className="cipb-footer">
              <span>{profile.confidentialityText}</span>
              <span>Page {index + 2} / {pageCount}</span>
            </footer>
          </div>
        ))}

        <div className="cipb-actions--footer">
          <Button icon={<CheckCircleOutlined />} onClick={applyJson}>Validate & apply JSON</Button>
          <Button
            icon={<UserOutlined />}
            onClick={() => {
              if (returnCandidateId) {
                navigate(`/candidate-search?candidate_id=${encodeURIComponent(returnCandidateId)}`);
                return;
              }
              navigate('/candidate-search');
            }}
          >
            Back to candidate
          </Button>
          <Button
            icon={<CheckCircleOutlined />}
            type={profile.profileApproved ? 'default' : 'primary'}
            disabled={Boolean(profile.profileApproved)}
            onClick={approveFinalProfile}
          >
            {profile.profileApproved ? 'Approved' : 'Approve final profile'}
          </Button>
          <Button icon={<EyeOutlined />} loading={pdfLoading} onClick={previewGeneratedPdf}>Preview PDF</Button>
          <Button icon={<DownloadOutlined />} loading={pdfLoading} onClick={exportPdf}>Generate PDF</Button>
          <Button icon={<MailOutlined />} loading={pdfLoading} onClick={openSendToClient}>Send to client</Button>
        </div>
      </main>

      <SendCVModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        data={generatedPdf ? {
          id: returnCandidateId,
          contactId: returnCandidateId,
          contactName: initialCvData?.contactName || initialCvData?.contact?.contactName || profile.displayName || displayName,
          title: initialCvData?.title || initialCvData?.contact?.title || profile.targetRole,
          email: initialCvData?.email || initialCvData?.contact?.email || '',
          phone: initialCvData?.phone || initialCvData?.phoneNumber || initialCvData?.contact?.phone || '',
          cvText: profile.assessment || profile.whyRelevant || '',
          skills: displayedKeySkills,
          evaluationSummary: profile.whyRelevant || profile.assessment || '',
          lastEvaluatedJob: initialCvData?.lastEvaluatedJob || initialCvData?.contact?.lastEvaluatedJob || null,
          attachmentPdfB64: generatedPdf.pdfB64,
          attachmentName: generatedPdf.filename
        } : null}
      />
    </div>
  );
};

export default CandidateIntelligenceProfileBuilder;
